import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  RadioGroup, FormControlLabel, Radio, FormLabel, Grid, Snackbar, Alert,
} from '@mui/material';
import {
  alterarTituloService, cadastrarTituloService,
} from '../../../../services/core/processos/processosTitulosService';
import {
  extractResponseErrorMessage, isValidationException,
} from '../../../../helpers/utils/form.utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível salvar o título.';
const ID_STATUS_BAIXADO = 3; // fixo — mesmo valor hardcoded do `statusTitulo` selector real (ver loadSelectors.saga.js)
// Campos que o backend pode devolver em `validations` — usado para decidir
// se um erro de validação é exibível campo a campo ou cai no Snackbar
// genérico, mesmo critério já documentado no CLAUDE.md do módulo (nunca só
// `if (validations)`/`if (validations?.length)`, que trata `[]` como
// "há erro" e produz um mapeamento vazio sem nenhuma mensagem visível).
const CAMPOS_FORMULARIO = [
  'idTipoTitulo', 'numero', 'dataVencimento', 'valorOriginal', 'valorProtesto',
  'idStatusTitulo', 'idMotivoBaixa',
];

/**
 * Port de `DialogTitulo.jsx`/`dialogTituloController.js` (tela real,
 * `src/pages/InspecionarProcesso/Titulos/DialogTitulo/`) para MUI v5 —
 * AW2-1034 Etapa 4 (Editar) — reaproveitado em modo criação na Etapa 6
 * (`isEditar=false`, sem `titulo`).
 *
 * Campos monetários (`valorOriginal`/`valorProtesto`) usam `TextField
 * type="number"` puro, sem máscara BRL — mesmo padrão já estabelecido em
 * `DialogAlterarParcela.jsx` (protótipo não tem `FieldModel`/máscara real).
 *
 * `tituloProtestado` não existe no DTO da LISTAGEM (`GET
 * {CORE}/processos/{id}/titulos` — confirmado na Etapa 1, lista completa de
 * campos documentada no plano de arquitetura) — só no payload de
 * escrita. Ao abrir para editar, o rádio é pré-selecionado por um proxy
 * (`titulo.protestado`, calculado como `valorProtesto > 0` desde a Etapa 1),
 * não o valor literal armazenado — mesma categoria de "campo sem fonte
 * confirmada, fallback documentado" já usada para as colunas "Protesto"/
 * "Total". Limitação aceita: um título com `valorProtesto === 0` mas
 * marcado como protestado no backend abre com o rádio em "Não".
 */
function makeFormFieldsTitulo(titulo) {
  return {
    idTitulo: titulo?.id ?? null,
    idTipoTitulo: titulo?.idTipoTitulo ?? '',
    numero: titulo?.numero ?? '',
    dataVencimento: titulo?.dataVencimentoIso ?? '',
    valorOriginal: titulo?.valorOriginal ?? '',
    valorProtesto: titulo?.valorProtesto ?? '',
    tituloProtestado: titulo?.protestado ? 'S' : 'N',
    idStatusTitulo: titulo?.idStatusTitulo ?? '',
    idMotivoBaixa: titulo?.idMotivoBaixa ?? '',
  };
}

function makePayloadTitulo(idProcesso, fields) {
  return {
    idProcesso,
    idTitulo: fields.idTitulo || null,
    idTipoTitulo: fields.idTipoTitulo || null,
    numero: fields.numero || null,
    dataVencimento: fields.dataVencimento || null,
    tituloProtestado: fields.tituloProtestado === 'S',
    valorOriginal: fields.valorOriginal === '' ? null : Number(fields.valorOriginal),
    valorProtesto: fields.valorProtesto === '' ? 0 : Number(fields.valorProtesto),
    idStatusTitulo: fields.idStatusTitulo || null,
    idMotivoBaixa: fields.idMotivoBaixa || null,
  };
}

/**
 * Mesma regra da tela real (`isDisableValorProtesto`, `DialogTitulo.jsx`):
 * "Valor Protesto" fica travado em `0` quando o título não é protestado, e
 * também quando é protestado mas o devedor está em SP/MG (regra fiscal da
 * tela real, não documentada em código, só replicada). `estadoDoDevedor`
 * reaproveita `sinalizacaoInfo.estadoDoDevedor` (já buscado por
 * `ControleProcessosPage.jsx`, mesmo endpoint que a tela real usa para o
 * mesmo dado — `additionalInfos.estadoDoDevedor` em
 * `InspecionarProcessoPage.jsx`) — nenhum fetch novo.
 */
function isValorProtestoDesabilitado(tituloProtestado, estadoDoDevedor) {
  if (tituloProtestado === 'N') {
    return true;
  }
  if (tituloProtestado === 'S' && (estadoDoDevedor === 'SP' || estadoDoDevedor === 'MG')) {
    return true;
  }
  return false;
}

export default function DialogTitulo({
  open, titulo, isEditar, idProcesso, estadoDoDevedor,
  tipoTituloOptions, statusTituloOptions, motivosBaixaOptions,
  onClose, onSucesso,
}) {
  const [formFields, setFormFields] = useState(() => makeFormFieldsTitulo(titulo));
  const [errosPorCampo, setErrosPorCampo] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  // Guarda de sessão/duplo-clique compartilhada — ver
  // `src/prototipo/hooks/useSessaoDialogMutacao.js`. `aoAbrir` reseta o
  // formulário só na transição real fechado -> aberto (nunca por
  // instabilidade de referência de `titulo`); `sessaoRef` invalida uma
  // resposta tardia de uma sessão anterior mesmo com o dialog fechado
  // (troca de `idProcesso` em voo).
  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setFormFields(makeFormFieldsTitulo(titulo));
    setErrosPorCampo({});
    setErro('');
    setEnviando(false);
  });

  const onChangeCampo = nome => (event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, [nome]: value }));
    setErrosPorCampo(erros => ({ ...erros, [nome]: undefined }));
  };

  const onChangeStatus = (event) => {
    const { value } = event.target;
    setFormFields(fields => ({
      ...fields,
      idStatusTitulo: value,
      idMotivoBaixa: value === ID_STATUS_BAIXADO ? fields.idMotivoBaixa : '',
    }));
    setErrosPorCampo(erros => ({ ...erros, idStatusTitulo: undefined }));
  };

  const onChangeTituloProtestado = (event) => {
    const { value } = event.target;
    setFormFields((fields) => {
      const forcarZero = value === 'N' || (value === 'S' && (estadoDoDevedor === 'SP' || estadoDoDevedor === 'MG'));
      return {
        ...fields,
        tituloProtestado: value,
        valorProtesto: forcarZero ? '0' : fields.valorProtesto,
      };
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (enviandoRef.current) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErrosPorCampo({});
    setErro('');

    try {
      const payload = makePayloadTitulo(idProcesso, formFields);
      if (formFields.idTitulo) {
        await alterarTituloService(payload);
      } else {
        await cadastrarTituloService(payload);
      }

      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onSucesso();
      onClose();
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      if (isValidationException(exception)) {
        const validations = exception.response.data.validations || [];
        const errosMapeados = validations.reduce((acc, validation) => (
          CAMPOS_FORMULARIO.includes(validation.field)
            ? { ...acc, [validation.field]: validation.message }
            : acc
        ), {});

        if (Object.keys(errosMapeados).length > 0) {
          setErrosPorCampo(errosMapeados);
        } else {
          setErro(MENSAGEM_ERRO_GENERICA);
        }
      } else {
        setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  const desabilitarValorProtesto = isValorProtestoDesabilitado(formFields.tituloProtestado, estadoDoDevedor);
  const desabilitarMotivoBaixa = formFields.idStatusTitulo !== ID_STATUS_BAIXADO;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEditar ? 'Editar Título' : 'Novo Título'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Tipo*"
                value={formFields.idTipoTitulo}
                onChange={onChangeCampo('idTipoTitulo')}
                error={Boolean(errosPorCampo.idTipoTitulo)}
                helperText={errosPorCampo.idTipoTitulo}
                size="small"
              >
                {tipoTituloOptions.map(opcao => (
                  <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número*"
                value={formFields.numero}
                onChange={onChangeCampo('numero')}
                error={Boolean(errosPorCampo.numero)}
                helperText={errosPorCampo.numero}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                type="date"
                fullWidth
                label="Vencimento*"
                value={formFields.dataVencimento}
                onChange={onChangeCampo('dataVencimento')}
                error={Boolean(errosPorCampo.dataVencimento)}
                helperText={errosPorCampo.dataVencimento}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                type="number"
                fullWidth
                label="Valor Original*"
                value={formFields.valorOriginal}
                onChange={onChangeCampo('valorOriginal')}
                error={Boolean(errosPorCampo.valorOriginal)}
                helperText={errosPorCampo.valorOriginal}
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                type="number"
                fullWidth
                label="Valor Protesto"
                value={formFields.valorProtesto}
                onChange={onChangeCampo('valorProtesto')}
                disabled={desabilitarValorProtesto}
                error={Boolean(errosPorCampo.valorProtesto)}
                helperText={errosPorCampo.valorProtesto}
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>Título Protestado</FormLabel>
              <RadioGroup row value={formFields.tituloProtestado} onChange={onChangeTituloProtestado}>
                <FormControlLabel value="S" control={<Radio size="small" />} label="Sim" />
                <FormControlLabel value="N" control={<Radio size="small" />} label="Não" />
              </RadioGroup>
            </Grid>

            {isEditar && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={formFields.idStatusTitulo}
                    onChange={onChangeStatus}
                    error={Boolean(errosPorCampo.idStatusTitulo)}
                    helperText={errosPorCampo.idStatusTitulo}
                    size="small"
                  >
                    {statusTituloOptions.map(opcao => (
                      <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Motivo da Baixa"
                    value={formFields.idMotivoBaixa}
                    onChange={onChangeCampo('idMotivoBaixa')}
                    disabled={desabilitarMotivoBaixa}
                    error={Boolean(errosPorCampo.idMotivoBaixa)}
                    helperText={errosPorCampo.idMotivoBaixa}
                    size="small"
                  >
                    {motivosBaixaOptions.map(opcao => (
                      <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onSubmit} variant="contained" disabled={enviando}>Confirmar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogTitulo.propTypes = {
  open: PropTypes.bool,
  titulo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idTipoTitulo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    numero: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dataVencimentoIso: PropTypes.string,
    valorOriginal: PropTypes.number,
    valorProtesto: PropTypes.number,
    protestado: PropTypes.bool,
    idStatusTitulo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idMotivoBaixa: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  isEditar: PropTypes.bool,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  estadoDoDevedor: PropTypes.string,
  tipoTituloOptions: PropTypes.array,
  statusTituloOptions: PropTypes.array,
  motivosBaixaOptions: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func.isRequired,
};

DialogTitulo.defaultProps = {
  open: false,
  titulo: null,
  isEditar: true,
  idProcesso: null,
  estadoDoDevedor: null,
  tipoTituloOptions: [],
  statusTituloOptions: [],
  motivosBaixaOptions: [],
};
